import { useMemo } from "react";

import type { DonorAssistantMessageSent } from "../../Chat/Chat";
import type { AssistantFeature } from "../../contexts/AssistantConfigContext";

import type { AssistantWidgetCloseTrigger } from "./useAssistantWidget";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { RouteType } from "@/common/hooks/amplitude/useLogRouteChangePageVisits";

export interface AssistantWidgetEvents {
  onOpen?: () => void;
  onClose?: (params: { messageCount: number; trigger: AssistantWidgetCloseTrigger }) => void;
  onMessageSent?: (params: DonorAssistantMessageSent) => void;
  onConversationRestarted?: () => void;
  onToolRendered?: (params: { toolName: string }) => void;
}

interface UseLogAssistantWidgetEventsProps {
  feature: AssistantFeature;
  routeType: RouteType;
  path: string;
}

export const useLogAssistantWidgetEvents = ({
  feature,
  routeType,
  path,
}: UseLogAssistantWidgetEventsProps): Required<AssistantWidgetEvents> => {
  const { logAmplitudeEvent } = useAmplitude();

  return useMemo<Required<AssistantWidgetEvents>>(() => {
    const context = { feature, routeType, path };
    return {
      onOpen: () => logAmplitudeEvent(AmplitudeEvents.AssistantWidgetOpened, { ...context, source: "launcher" }),
      onClose: ({ messageCount, trigger }) =>
        logAmplitudeEvent(AmplitudeEvents.AssistantWidgetClosed, { ...context, messageCount, trigger }),
      onMessageSent: ({ turnIndex, source }) =>
        logAmplitudeEvent(AmplitudeEvents.AssistantMessageSent, { ...context, turnIndex, source }),
      onConversationRestarted: () => logAmplitudeEvent(AmplitudeEvents.AssistantConversationRestarted, context),
      onToolRendered: ({ toolName }) =>
        logAmplitudeEvent(AmplitudeEvents.AssistantToolRendered, { ...context, toolName }),
    };
  }, [logAmplitudeEvent, feature, routeType, path]);
};
